import React, { useState, useEffect, useMemo } from 'react';
import {
    Search, ChevronRight, ArrowLeft, Wand2, CheckCircle, AlertTriangle, Cpu, Calendar, FileText, Loader, Clock, Trash2,
    LayoutDashboard, Layers, PenSquare, Users, SearchCode, Send
} from 'lucide-react';
import apiService from '../../services/apiService';
import { PageLoader } from '../../components/PageLoader';
import { ErrorDisplay } from '../../components/ErrorDisplay';

// --- Reusable UI Components ---
const Card = ({ children, className = '' }) => (
    <div className={`bg-slate-800 border border-slate-700 rounded-lg shadow-md ${className}`}>
        {children}
    </div>
);

const Button = ({ children, onClick, className = '', variant = 'primary', icon: Icon, disabled = false }) => {
    const baseClasses = 'flex items-center justify-center gap-2 px-4 py-2 rounded-md font-semibold text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed';
    const variants = {
        primary: 'bg-brand-500 text-white hover:bg-brand-400',
        secondary: 'bg-slate-600 text-slate-100 hover:bg-slate-500',
    };
    return (
        <button onClick={onClick} disabled={disabled} className={`${baseClasses} ${variants[variant]} ${className}`}>
            {Icon && <Icon size={16} />}
            {children}
        </button>
    );
};

const StatCard = ({ title, value, icon, color }) => (
    <Card className="p-4">
        <div className="flex items-center gap-4">
            <div className={`p-3 bg-slate-900/50 rounded-lg ${color}`}>{icon}</div>
            <div>
                <p className="text-slate-400 text-sm">{title}</p>
                <p className="text-2xl font-bold text-white">{value}</p>
            </div>
        </div>
    </Card>
);

// --- Main SEO Audit Component ---
export default function SeoAuditView({ setActiveView, onCreateTask }) {
    const [view, setView] = useState('initial');
    const [history, setHistory] = useState([]);
    const [activeReport, setActiveReport] = useState(null);
    const [selectedIssue, setSelectedIssue] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [sitemapUrl, setSitemapUrl] = useState('');
    const [auditName, setAuditName] = useState('');
    const [auditType, setAuditType] = useState('regular');

    const loadHistory = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const auditHistory = await apiService.fetchAuditHistory();
            setHistory(auditHistory);
        } catch (err) {
            setError(err.message || 'Failed to load audit history.');
        }
        setIsLoading(false);
    };

    useEffect(() => {
        loadHistory();
    }, []);

    useEffect(() => {
        const runningAudit = history.find(r => r.status === 'in_progress');
        if (runningAudit) {
            const interval = setInterval(async () => {
                try {
                    const updatedReport = await apiService.fetchAuditReport(runningAudit._id);
                    if (activeReport?._id === updatedReport._id) {
                        setActiveReport(updatedReport);
                    }
                    if (updatedReport.status === 'completed' || updatedReport.status === 'error') {
                        clearInterval(interval);
                        loadHistory();
                    }
                } catch (err) {
                     console.error("Polling failed:", err);
                     clearInterval(interval);
                     loadHistory();
                }
            }, 5000);
            return () => clearInterval(interval);
        }
    }, [history, activeReport]);

    const handleStartAudit = async (isSitemap = false) => {
        setIsLoading(true);
        setError(null);
        try {
            const urlForAudit = isSitemap ? sitemapUrl : null;
            const name = auditName.trim() || `New ${auditType} audit on ${new Date().toLocaleDateString()}`;
            const initialReport = await apiService.startSiteWideAudit(name, urlForAudit, auditType);
            setHistory(prevHistory => [initialReport, ...prevHistory]);
            setAuditName('');
            setSitemapUrl('');
            setActiveReport(initialReport);
            setView('progress');
        } catch (err) {
            setError(err.message || 'The audit could not be started.');
            setView('initial');
        } finally {
            setIsLoading(false);
        }
    };
    
    const handleViewReport = async (reportId) => {
        if (!reportId) return;
        setIsLoading(true);
        setError(null);
        try {
            const reportData = await apiService.fetchAuditReport(reportId);
            setActiveReport(reportData);
            setView('summary');
        } catch (err) {
            setError(err.message || 'Failed to load the selected report.');
        }
        setIsLoading(false);
    };
    
    const handleDeleteAudit = async (e, reportId) => {
        e.stopPropagation();
        if (window.confirm("Are you sure you want to permanently delete this audit report?")) {
            try {
                await apiService.deleteAuditReport(reportId);
                setHistory(prevHistory => prevHistory.filter(report => report._id !== reportId));
            } catch (err) {
                alert(`Failed to delete report: ${err.message}`);
            }
        }
    };

    const handleSendToFlightDeck = async (page) => {
        const title = `Fix SEO Issue: ${selectedIssue.title}`;
        const description = `
**URL:** ${page.url}
**Current Value:** "${page.currentValue}"
**AI Analysis:** ${page.aiAnalysis || 'N/A'}
---
**AI Recommendation:** "${page.aiRecommendation}"
        `.trim();

        const taskData = {
            title: title,
            origin: 'SEO Audit',
            description: description
        };

        if (onCreateTask) {
            await onCreateTask(taskData);
        } else {
            console.error("onCreateTask prop is not available.");
            alert("Could not communicate with parent component to create task.");
        }
    };

    const estimatedTime = useMemo(() => {
        if (!activeReport?.totalPagesAudited) return '';
        const pagesCompleted = activeReport.pagesAudited || 0;
        const pagesRemaining = activeReport.totalPagesAudited - pagesCompleted;
        if (pagesRemaining <= 0) return 'finishing up...';
        const averageTimePerPage = 3;
        const totalSeconds = pagesRemaining * averageTimePerPage;
        if (totalSeconds < 60) return `about ${Math.ceil(totalSeconds)} seconds remaining`;
        return `about ${Math.ceil(totalSeconds / 60)} minutes remaining`;
    }, [activeReport]);

    const renderInitialView = () => (
        <div className="animate-fade-in space-y-6">
            <Card className="p-6">
                <h2 className="text-xl font-bold text-white mb-4">Run a New Audit</h2>
                <div className="mb-4">
                    <label className="block text-sm font-medium text-slate-300 mb-2">Select Audit Type</label>
                    <div className="grid grid-cols-2 gap-2">
                        <button onClick={() => setAuditType('regular')} className={`p-3 text-center rounded-lg border transition-colors ${auditType === 'regular' ? 'bg-brand-500/20 border-brand-500 text-white' : 'border-slate-600 text-slate-300 hover:bg-slate-700'}`}>Regular SEO</button>
                        <button onClick={() => setAuditType('technical')} className={`p-3 text-center rounded-lg border transition-colors ${auditType === 'technical' ? 'bg-brand-500/20 border-brand-500 text-white' : 'border-slate-600 text-slate-300 hover:bg-slate-700'}`}>Technical SEO</button>
                    </div>
                </div>
                <div className="space-y-4">
                    <div>
                        <label htmlFor="audit-name" className="block text-sm font-medium text-slate-300 mb-2">Audit Name (Optional)</label>
                        <input type="text" id="audit-name" value={auditName} onChange={(e) => setAuditName(e.target.value)} className="w-full bg-slate-700 border border-slate-600 text-white rounded-md p-3 focus:ring-2 focus:ring-brand-500 focus:outline-none" placeholder="e.g., Q3 Content Audit" />
                    </div>
                    <Button onClick={() => handleStartAudit(false)} disabled={isLoading} icon={Search} className="w-full">
                        {isLoading ? 'Processing...' : `Start ${auditType === 'technical' ? 'Technical' : 'Regular'} Audit with WordPress Data`}
                    </Button>
                    <div className="relative flex items-center justify-center py-2">
                        <div className="flex-grow border-t border-slate-700"></div><span className="flex-shrink mx-4 text-slate-500 text-xs uppercase">OR</span><div className="flex-grow border-t border-slate-700"></div>
                    </div>
                    <div>
                        <label htmlFor="sitemap-url" className="block text-sm font-medium text-slate-300 mb-2">Provide a Sitemap URL</label>
                        <div className="flex gap-2">
                            <input type="url" id="sitemap-url" value={sitemapUrl} onChange={(e) => setSitemapUrl(e.target.value)} className="w-full bg-slate-700 border border-slate-600 text-white rounded-md p-3 focus:ring-2 focus:ring-brand-500 focus:outline-none" placeholder="https://example.com/sitemap.xml" />
                            <Button onClick={() => handleStartAudit(true)} disabled={isLoading || !sitemapUrl} variant="secondary">Audit Sitemap</Button>
                        </div>
                    </div>
                </div>
            </Card>
            <Card className="!p-0">
                <div className="p-6"><h2 className="text-2xl font-bold text-white">Audit History</h2></div>
                {isLoading && history.length === 0 && <div className="text-center p-8"><Loader className="w-8 h-8 mx-auto animate-spin" /></div>}
                {!isLoading && history.length === 0 && <p className="p-6 text-center text-slate-400">No previous audits found.</p>}
                <ul className="divide-y divide-slate-700">
                   {history.map(report => {
                        const now = new Date();
                        const updatedAt = new Date(report.updatedAt);
                        const isStale = report.status === 'in_progress' && (now - updatedAt) > 5 * 60 * 1000;
                        return (
                           <li key={report._id} onClick={() => report.status === 'completed' && handleViewReport(report._id)} className={`p-6 group ${report.status === 'completed' ? 'hover:bg-slate-700/50 cursor-pointer' : 'opacity-70'}`}>
                               <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                                   <div className="flex items-center gap-4">
                                        {(report.status === 'in_progress' && !isStale) && <Loader className="w-8 h-8 text-brand-400 flex-shrink-0 animate-spin" />}
                                        {(report.status === 'error' || isStale) && <AlertTriangle className="w-8 h-8 text-red-400 flex-shrink-0" />}
                                        {report.status === 'completed' && <Calendar className="w-8 h-8 text-brand-400 flex-shrink-0" />}
                                        <div>
                                           <p className="font-bold text-white text-lg">{report.name}</p>
                                           <p className="text-sm text-slate-400">{report.status === 'completed' ? `Completed on ${new Date(report.createdAt).toLocaleDateString()}` : isStale ? 'Job is stale or has timed out' : `${report.pagesAudited || 0} / ${report.totalPagesAudited || 0} pages audited`}</p>
                                       </div>
                                   </div>
                                   {(report.status === 'in_progress' && !isStale) ? ( <div className="flex items-center gap-2 text-sm text-brand-400"><Clock size={14}/><span>In Progress...</span></div>
                                   ) : (report.status === 'error' || isStale) ? (
                                        <div className="flex items-center gap-2"><span className="text-sm font-medium text-red-400">{isStale ? 'Abort Job' : 'Audit Failed'}</span><button onClick={(e) => handleDeleteAudit(e, report._id)} className="p-2 rounded-full text-slate-400 hover:text-white hover:bg-red-500/20"><Trash2 size={16} /></button></div>
                                   ) : (
                                       <div className="flex items-center gap-4 text-sm w-full sm:w-auto"><span className="flex items-center gap-1 text-red-400"><AlertTriangle size={14} /> {report.issueCounts.critical}</span><span className="flex items-center gap-1 text-yellow-400"><AlertTriangle size={14} /> {report.issueCounts.warning}</span><span className="flex items-center gap-1 text-blue-400"><CheckCircle size={14} /> {report.issueCounts.recommendation}</span><div className="ml-auto sm:ml-4 flex items-center gap-4"><p className="text-lg font-bold text-green-400">{report.overallScore}/100</p><button onClick={(e) => handleDeleteAudit(e, report._id)} className="p-2 rounded-full text-slate-500 hover:text-red-400 hover:bg-red-500/10 transition-colors"><Trash2 size={16} /></button><ChevronRight className="text-slate-500"/></div></div>
                                   )}
                               </div>
                           </li>
                        );
                   })}
                </ul>
            </Card>
        </div>
    );
    
    const renderProgressView = () => {
        const pagesCompleted = activeReport?.pagesAudited || 0;
        const totalPages = activeReport?.totalPagesAudited || 0;
        const progressPercentage = totalPages > 0 ? (pagesCompleted / totalPages) * 100 : 0;
        return (
            <Card className="p-8">
                <div className="flex flex-col items-center justify-center h-full max-w-2xl mx-auto">
                    <h2 className="text-3xl font-bold text-white mb-2 text-center">Running Site-Wide Audit...</h2><p className="text-slate-400 mb-6">{activeReport?.name}</p><div className="w-full"><div className="flex justify-between items-center mb-1 text-slate-300"><span>Progress</span><span className="font-medium">{pagesCompleted} / {totalPages} pages</span></div><div className="w-full bg-slate-700 rounded-full h-2.5"><div className="bg-brand-400 h-2.5 rounded-full transition-all duration-500" style={{ width: `${progressPercentage}%` }}></div></div><div className="text-center mt-3 text-brand-400 font-medium"><Clock size={16} className="inline-block mr-2" /><span>{estimatedTime}</span></div></div>
                    <div className="mt-12 text-center p-4 bg-slate-900/50 rounded-lg border border-slate-700"><p className="text-slate-300">You can safely navigate away. The audit will continue in the background.</p></div>
                </div>
            </Card>
        );
    };
    
    const renderSummaryView = () => {
        if (!activeReport) return <div className="text-center p-8"><Loader className="w-8 h-8 mx-auto animate-spin" /></div>;
        return (
            <div className="animate-fade-in">
                <header className="flex items-center justify-between mb-6"><Button onClick={() => { setActiveReport(null); setView('initial'); }} variant="secondary" icon={ArrowLeft}>Back to History</Button></header>
                <div className="text-center mb-8"><h2 className="text-3xl font-bold text-white">{activeReport.name}</h2><p className="text-sm text-slate-400 mt-1">Completed on {new Date(activeReport.createdAt).toLocaleString()}</p></div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6"><StatCard title="Overall Score" value={`${activeReport.overallScore}/100`} icon={<FileText size={28}/>} color="text-green-400" /><StatCard title="Critical Issues" value={activeReport.issueCounts.critical} icon={<AlertTriangle size={28}/>} color="text-red-400" /><StatCard title="Warnings" value={activeReport.issueCounts.warning} icon={<AlertTriangle size={28}/>} color="text-yellow-400" /><StatCard title="Recommendations" value={activeReport.issueCounts.recommendation} icon={<CheckCircle size={28}/>} color="text-blue-400" /></div>
                <Card className="mt-8 !p-0"><div className="p-6 border-b border-slate-700"><h3 className="text-xl font-bold text-white">Issues Found</h3></div><ul className="divide-y divide-slate-700">{activeReport.issues.length === 0 && <li className="p-6 text-center text-slate-400">No issues found. Great job!</li>}{activeReport.issues.map((issue) => (<li key={issue.type} onClick={() => { setSelectedIssue(issue); setView('detail'); }} className="p-4 sm:p-6 hover:bg-slate-700/50 cursor-pointer flex justify-between items-center transition-colors"><div><h4 className="font-semibold text-white">{issue.title}</h4><p className="text-sm text-slate-400">{issue.affectedPages.length} pages affected</p></div><ChevronRight className="text-slate-500"/></li>))}</ul></Card>
            </div>
        );
    };

    const renderDetailView = () => {
        if (!selectedIssue) return null;
        return (
             <div className="animate-fade-in">
                <header className="flex items-center mb-6"><Button onClick={() => setView('summary')} variant="secondary" className="!p-2"><ArrowLeft /></Button><div className="ml-4"><h2 className="text-2xl font-bold text-white">{selectedIssue.title}</h2></div></header>
                <Card className="!p-0"><div className="p-6 border-b border-slate-700"><h3 className="text-xl font-bold text-white">Affected Pages & Recommendations</h3></div><div className="overflow-x-auto"><table className="w-full text-left"><thead className="text-xs text-slate-400 uppercase bg-slate-900/50"><tr><th className="p-4">URL</th><th className="p-4">Current Value / Analysis</th><th className="p-4">AI Recommendation</th><th className="p-4">Action</th></tr></thead><tbody className="divide-y divide-slate-700">{selectedIssue.affectedPages.map((page, index) => (<tr key={index} className="hover:bg-slate-700/50 transition-colors"><td className="p-4 text-white font-medium text-sm align-top">{page.url}</td><td className="p-4 text-sm align-top"><code className="font-mono text-slate-400 bg-slate-900/50 rounded p-1">"{page.currentValue}"</code>{page.aiAnalysis && <p className="mt-2 text-slate-300 italic">Analysis: {page.aiAnalysis}</p>}</td><td className="p-4 text-sm text-teal-300 font-mono align-top">"{page.aiRecommendation}"</td><td className="p-4 align-top"><Button onClick={() => handleSendToFlightDeck(page)} variant="primary" className="!text-xs !px-3 !py-1" icon={Send}>Send to Flight Deck</Button></td></tr>))}</tbody></table></div></Card>
            </div>
        );
    };
    
    const renderContent = () => {
        switch (view) {
            case 'progress': return renderProgressView();
            case 'summary': return renderSummaryView();
            case 'detail': return renderDetailView();
            case 'initial':
            default: return renderInitialView();
        }
    };
    
    const navItems = [
        { id: 'foundry', label: 'Dashboard', icon: LayoutDashboard },
        { id: 'campaigns', label: 'Campaign Builder', icon: Layers },
        { id: 'foundryContentCreation', label: 'Content Creator', icon: PenSquare },
        { id: 'foundryCompetitorAnalysis', label: 'Competitor Analysis', icon: Users },
        { id: 'seoAudit', label: 'SEO Audit', icon: SearchCode },
    ];
    
    return (
        <div className="animate-fade-in">
             <header className="flex flex-col md:flex-row justify-between md:items-center gap-4 border-b border-slate-700 pb-4 mb-6">
                <h1 className="text-3xl font-bold text-white">Foundry</h1>
                <div className="bg-slate-800 p-1 rounded-lg flex items-center gap-1 flex-wrap">
                    {navItems.map(item => (
                        <button
                            key={item.id}
                            onClick={() => setActiveView(item.id)}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-md text-sm font-semibold transition-colors duration-200 ${
                                'seoAudit' === item.id ? 'bg-teal-700 text-white' : 'text-slate-300 hover:bg-slate-700'
                            }`}
                        >
                            <item.icon size={16} />
                            {item.label}
                        </button>
                    ))}
                </div>
            </header>
            
            {error && <div className="mb-6"><ErrorDisplay title="An Error Occurred" message={error} onDismiss={() => setError(null)} /></div>} 
            {renderContent()} 
        </div>
    );
}